import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import AppInput from "components/ui/AppInput";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import CitiesList from "../components/CitiesList";

import { AreasAction, LoaderAction } from "redux/actions";
import ChooseTime from "../../common/ChooseTime";
import { errorHandler } from "api/api/errorHandler";
import TextInput from "components/TextInput";

import { isValidInputFields } from "features/shipping_methods/helpers";

import { UTILS } from "utils";
import { API } from "api";
import useGetStoreAddedMappedAreas from "./hooks/useGetStoreAddedMapAreas";
import { errorNotification } from "utils/Utils";
import { calculateDeliveryTimeInMins } from "../../helpers";
import AppSelect from "components/ui/AppSelect";
import AppModal from "components/ui/AppModal";
const AddNewMapAreas = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getStoreMappedAddedAreas = useGetStoreAddedMappedAreas();

  const [searchTerm, setSearch] = useState("");
  const [openAreas, setOpenAreas] = useState(false);

  const { newSelectedMappedAreas, mapCustomBusinessAreas, selectedStore } =
    useSelector((state) => state.AreasReducer);
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const [selectedAreaState, setState] = useState({
    minOrder: "",
    deliveryFee: "",
    deliveryTime: {
      Hours: 0,
      Minutes: 0,
      Days: 0,
    },
  });

  const handleValues = async (key, value) =>
    setState({ ...selectedAreaState, [key]: value });

  const fetchAddedAreas = async (storeID) => {
    try {
      await getStoreMappedAddedAreas({ storeId: storeID });
      onClose();
      dispatch(LoaderAction.setLoading(false));
    } catch (error) {
      UTILS.errorNotification(t(errorHandler(error)));
    } finally {
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const selectedAreasArray = newSelectedMappedAreas?.map((area) => ({
    mappedAreaId: area?.area?._id,
    translations: {
      en: { name: area?.area?.translations?.en?.name },
      ar: { name: area?.area?.translations?.ar?.name },
    },
    city: {
      en: area?.city?.en,
      ar: area?.city?.ar,
    },
    country: {
      en: area?.country?.en,
      ar: area?.country?.ar,
    },
  }));

  const addNewMappedAreas = async () => {
    try {
      const { error, isValid } = isValidInputFields({
        isLoading,
        state: selectedAreaState,
        selectedAreas: newSelectedMappedAreas,
      });
      if (!isValid) {
        errorNotification(error);
        return;
      }

      dispatch(LoaderAction.setLoading(true));
      const deliveryTimeInMins = calculateDeliveryTimeInMins({
        deliveryTime: selectedAreaState.deliveryTime,
      });
      const storeID = selectedStore.value;
      const userDetail = UTILS.getCurrentUser();
      const result = await API.addStoreMappedArea({
        mappedAreasInfo: selectedAreasArray,
        minOrder: Number(selectedAreaState.minOrder),
        deliveryFee: Number(selectedAreaState.deliveryFee),
        deliveryTime: Number(deliveryTimeInMins),
        storeId: storeID,
        brandId: userDetail.brandId,
      });
      if (result?.success === 1) {
        UTILS.successNotification(t(result.message));
        dispatch(AreasAction.setNewMappedAreas([]));
      } else {
        UTILS.errorNotification(t(errorHandler(result)));
      }
      await fetchAddedAreas(selectedStore.value);
    } catch (error) {
      UTILS.errorNotification(t(errorHandler(error)));
    } finally {
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const handleSearch = (val) => {
    setSearch(val.toLowerCase());
    if (val !== "") setOpenAreas(true);
    else setOpenAreas(false);
  };

  const handleInputValue = (key, val) =>
    handleValues(key, UTILS.NumberConversion(val));

  useEffect(() => {
    if (selectedStore?.value)
      getStoreMappedAddedAreas({
        storeId: selectedStore.value,
      });
  }, [selectedStore?.value]);

  const daysOptions = Array.from({ length: 8 }, (_, value) => ({
    label: `${value} ${t("Days")}`,
    value,
  }));
  const hourOptions = Array.from({ length: 24 }, (_, value) => ({
    label: `${value} ${t("Hours")}`,
    value,
  }));
  const minuteOptions = Array.from({ length: 12 }, (_, index) => ({
    label: `${index * 5} ${t("Minutes")}`,
    value: index * 5,
  }));

  const footer = (
    <div className="mt-4 flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={isLoading}>
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton variant="primary" onClick={addNewMappedAreas} loading={isLoading}>
        {t("Add Areas")}
      </AppButton>
    </div>
  );
  return (
    <AppModal
      open={open}
      onClose={onClose}
      width={620}
      title={t("Add delivery areas")}
      footer={footer}
    >
      <div>
        <div className="grid grid-cols-2 gap-4">
          <AppInput
            label={t("Min. Basket Value")}
            mandatory={true}
            type="number"
            value={selectedAreaState.minOrder}
            onInput={(e) => handleInputValue("minOrder", e.target.value)}
          />
          <AppInput
            label={t("Delivery Fee")}
            mandatory={true}
            type="number"
            value={selectedAreaState.deliveryFee}
            onInput={(e) => handleInputValue("deliveryFee", e.target.value)}
          />
        </div>
        <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
        <div className="grid grid-cols-3 gap-4">
          <AppSelect
            placeholder={t("Select Days")}
            value={selectedAreaState.deliveryTime.Days}
            options={daysOptions}
            onChange={(value) =>
              handleValues("deliveryTime", {
                ...selectedAreaState.deliveryTime,
                Days: Number(value || 0),
              })
            }
          />
          <AppSelect
            placeholder={t("Select Hours")}
            value={selectedAreaState.deliveryTime.Hours}
            options={hourOptions}
            onChange={(value) =>
              handleValues("deliveryTime", {
                ...selectedAreaState.deliveryTime,
                Hours: Number(value || 0),
              })
            }
          />
          <AppSelect
            placeholder={t("Select Minutes")}
            value={selectedAreaState.deliveryTime.Minutes}
            options={minuteOptions}
            onChange={(value) =>
              handleValues("deliveryTime", {
                ...selectedAreaState.deliveryTime,
                Minutes: Number(value || 0),
              })
            }
          />
        </div>
        <div className="my-3 text-lg font-bold">
          <span className="mb-2 block">{t("Search Area Name")}</span>
          <AppSearchInput
            value={searchTerm}
            placeholder={t("Search")}
            onInput={(e) => handleSearch(e.target.value)}
          />
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 p-2">
          {mapCustomBusinessAreas?.map((country) =>
            country?.cities?.map((city, index) => (
              <CitiesList
                cityCountry={country.name}
                openAreas={openAreas}
                searchTerm={searchTerm}
                key={index}
                city={city?.name}
                cityAreas={city?.areas}
              />
            ))
          )}
        </div>
        
      </div>
    </AppModal>
  );
};

AddNewMapAreas.propTypes = {
  handleValues: PropTypes.func,
  onClose: PropTypes.func,
};
export default AddNewMapAreas;
